import { useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RouteBoundary } from './Boundary.tsx';
import { OutageBanner } from './Faults.tsx';
import { SupplyStrip } from './Strip.tsx';
import { SurfaceLink, useLink } from './read.ts';
import { useRelink, useSources } from './sources.ts';
import styles from './surface.module.css';
/* The reduced-motion form of the console's one indeterminate bar. Global, and
   imported from the two components that are in the tree on every route. */
import './motion.css';

/* ==========================================================================
   A route, and what to do with it when the reads stop.

   THE ONE RULE THIS FILE EXISTS TO ENFORCE: a failed read never replaces the
   route. Not the first one, not the hundredth.

   The console had two failure languages and they contradicted each other. If
   figures were already on screen it kept them, dimmed them, and marked each
   panel — the right answer, and the one operators live in. If the FIRST read
   failed it did the opposite: it painted a page of diagnostics over the whole
   canvas, so the operator lost the breadcrumb, the tab strip, the column
   headers and the reserved cells, and got 46% of a 1600×1000 viewport of bare
   charcoal in exchange.

   They are the same event. The console has stopped being supplied. The only
   difference between them is whether the figures under the fault line are
   real or are the instrument the loading state was already drawing — and that
   difference is worth one veil, not two designs.

   So the surface always renders its own layout at full width, and the
   diagnostics take a fixed band at the foot of the screen:

     26px   BANNER   the verdict, one sentence, the countdown, ONE verb
     ...    ROUTE    every pixel it had, untouched
     24px   SUPPLY   the browser's own account of itself, on one line, with the
                     196px ledger — sources · console · read attempts — one
                     keystroke behind it

   Fifty pixels, not two hundred and forty, and never more without the operator
   asking. The route's grid has fixed-content bands in it, and taking a fifth
   of its height away compresses them past their content: a partial outage used
   to overflow the fleet KPI band into the panel below it, which is the failure
   design breaking the layout it exists to protect.

   Both appear the moment a source stops being supplied and vanish the moment
   nothing is. Neither ever costs the route a column of width, because an
   outage is precisely when the fleet table needs its columns.
   ========================================================================== */

export function Surface({
  /** The route, exactly as it renders when everything is answering. */
  children,
  /** Named in the strip's CONSOLE column, so the ledger says whose it is. */
  surface,
  /** Suppress the diagnostics — for a surface that is itself a diagnostic. */
  rail = true,
  /** Offered as the recovery when the fault is a 401. */
  onSignIn,
}: {
  children: ReactNode;
  surface?: string | undefined;
  rail?: boolean | undefined;
  onSignIn?: (() => void) | undefined;
}) {
  /* `useLink` is `useOutage` plus the one word the palette is keyed on, so the
     banner's picture and the route's verdict are read off one call and cannot
     disagree. */
  const outage = useLink();
  const sources = useSources();
  const qc = useQueryClient();
  /* A read that gave up at the transport layer is tried again as soon as some
     other read demonstrably gets through — otherwise the one-shot sources a
     surface reads at mount stay failed for the rest of the session and the
     banner above can never clear itself. See `useRelink`. */
  useRelink();

  const retryAll = useCallback(() => {
    void qc.refetchQueries({ type: 'active' });
  }, [qc]);

  /* TanStack only settles a query into `error` once its retries are spent, so
     a source that is merely mid-blip is not counted here and the chrome does
     not flash in and out on a dropped packet. */
  const faulted = rail && sources.some((s) => s.supply);

  /* THE VERDICT, PUBLISHED ON THE ROUTE ITSELF.

     One place decides whether this surface is being supplied, and everything
     inside it inherits the answer — see the palette demotion in
     `surface.module.css`. It is an attribute rather than a context because the
     thing that has to change is a COLOUR, in components this module does not
     own and cannot re-render, and a custom property crossing a subtree is the
     only mechanism in the platform that reaches all of them at once.

       up        every mounted source is answering
       partial   some endpoints are out, the rest are fine — the panels that
                 stopped mark themselves, and the ones still reading are still
                 telling the truth, so the surface keeps its palette
       refused   the API answers every read with a fault
       down      nothing is arriving at all

     `refused` and `down` are the two that mean NOT ONE FIGURE ON THIS SCREEN
     IS CURRENT. */
  /* `rail={false}` is a surface that is itself a diagnostic; it keeps its own
     palette so the thing diagnosing the outage does not wear it. */
  const link = rail ? outage.state : 'up';

  return (
    <div
      className={styles.surface}
      data-surface="true"
      data-faulted={faulted}
      data-link={link}
    >
      {faulted && (
        <OutageBanner
          outage={outage}
          sources={sources}
          onRetry={retryAll}
          {...(onSignIn !== undefined ? { onSignIn } : {})}
        />
      )}
      {/* The route body, and the only boundary in the tree. Without it a
          single unguarded field in one panel takes the WHOLE console to a
          white viewport — no header, no clock, no way back — and the exception
          lands in the developer console where no operator will read it. With
          it, the shell, the banner and the supply line survive and the route
          says what happened in the same words every stopped panel uses. */}
      <div className={styles.body} data-surface-body="true">
        {/* The verdict, downward. Every panel inside asks it one question —
            "has the banner already named this endpoint?" — and stops repeating
            it when the answer is yes. See `useBannerCarries`. */}
        <SurfaceLink.Provider value={link}>
          <RouteBoundary where={surface ?? 'this view'}>{children}</RouteBoundary>
        </SurfaceLink.Provider>
      </div>
      {faulted && (
        /* WHEN THE LEDGER OPENS ITSELF, AND WHEN IT MUST NOT.

           Two wrong answers were tried before this one. It used to open on any
           failure, and on a PARTIAL outage that is indefensible: three sources
           out of eight, the fleet register among the five that are fine,
           thirteen robots needing attention on screen — and the console bills
           a fifth of the viewport to a ledger about the other three. Then it
           never opened at all, and that is worse in the case that matters: on
           a surface where NOTHING is answering the route below is drawing its
           column headings over several hundred pixels of hatch, and the one
           instrument that says which endpoints stopped, how often each is
           read, when each last worked and when the next attempt lands was
           folded into a 20px line at the foot — hidden during the only four
           minutes in a month anybody wants it.

           So it opens itself in exactly one case, and the case is defined by
           what is UNDER it rather than by how bad the outage is:

             stopped   `down` or `refused` — not one figure on this surface is
                       current, so the banner is speaking for every panel
             cold      nothing has landed this session, so every panel body is
                       its instrument over hatched ground and there is no
                       last-known fleet on screen to cover

           Both, or it stays a line. A STALLED surface fails the second test
           and that is the point: 30 seconds into an outage /fleet is holding
           the last known census, the last charge histogram and the thirteen
           robots that need attention, and those are the pixels an operator is
           actually working from. Covering them with a ledger about the link
           would be this component doing the thing it exists to prevent.

           And when it opens itself it is capped shorter than the form the
           operator asks for (strip.module.css): 136px rather than 192px,
           because this height is coming out of a route nobody chose to shrink.
           The full-height ledger, and a scroller-free eight-source list, are
           one click away on the same button.

           The operator's own choice always wins over both (see `opened` in
           Strip.tsx) — including closing it on a cold surface. */
        <SupplyStrip
          cold={(link === 'down' || link === 'refused') && outage.sinceMs === null}
          {...(surface !== undefined ? { surface } : {})}
        />
      )}
    </div>
  );
}
